// ============================================================
// Grades dus par les achats Tebex : lecture de l'export, nettoyage, règles de la boutique
// ============================================================
// Fonctions pures (sans I/O ni store), exécutées dans le NAVIGATEUR. L'export des paiements de Tebex
// contient des données personnelles (nom, adresse, pays, e-mail, IP, montants). Elles sont écartées dès
// la lecture : seules les colonnes de ALLOWED_COLUMNS sont gardées, les autres ne sont ni conservées,
// ni affichées, ni envoyées. Le satellite ne reçoit que le XUID, le pseudo Tebex, les grades dus avec
// leur fin, les grades achetés un jour et les achats qui les expliquent (numéro, date, produit).

/** Colonnes gardées de l'export ; toute autre colonne est jetée à la lecture. */
export const ALLOWED_COLUMNS = [
  'Id', 'Transaction ID', 'Date', 'Status', 'Gateway', 'Username', 'Uuid', 'Packages', 'Package Names',
] as const;

/** Grades que la pose peut écrire (les grades vendus, miroir de REPAIR_GRADES du satellite). */
export type SoldGrade = 'divinite_gold' | 'divinite' | 'legende' | 'heros' | 'premium' | 'luckypass' | 'pionnier' | 'paladin' | 'titan';
export const SOLD_GRADES: SoldGrade[] = ['divinite_gold', 'divinite', 'legende', 'heros', 'premium', 'luckypass', 'pionnier', 'paladin', 'titan'];

/** Rang des grades qui se remplacent (le plus haut l'emporte) ; Premium et LuckyPass s'ajoutent. */
export const GRADE_RANK: Partial<Record<SoldGrade, number>> = { titan: 1, paladin: 2, heros: 4, legende: 5, divinite: 6, divinite_gold: 7 };

/** Un paiement, réduit aux colonnes gardées. */
export interface TebexPayment {
  id: string;
  transactionId: string;
  /** Date ISO de Tebex (UTC). */
  date: string;
  status: string;
  gateway: string;
  username: string;
  /** Colonne Uuid de Tebex : c'est le XUID Xbox (16 chiffres), pas l'uuid de LuckPerms. */
  xuid: string;
  packages: string[];
  packageNames: string[];
}

export interface CleanedExport {
  payments: TebexPayment[];
  /** Noms des colonnes écartées (les noms seulement, jamais leur contenu). */
  dropped: string[];
  /** Colonnes attendues absentes de l'export. */
  missing: string[];
}

/** CSV (RFC 4180) : guillemets doublés, séparateurs et retours à la ligne entre guillemets, BOM. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  const s = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (quoted) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += c;
    } else if (c === '"') {
      quoted = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.length > 1 || row[0] !== '') rows.push(row);
      row = [];
    } else field += c;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows;
}

/** Les noms de produits de Tebex sont entre apostrophes, séparés par des virgules. */
function splitNames(raw: string): string[] {
  const out: string[] = [];
  const re = /'([^']*)'/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw))) out.push(m[1]);
  return out.length ? out : raw.split(',').map((x) => x.trim()).filter(Boolean);
}

/**
 * Export lu et réduit aux colonnes gardées. Les autres colonnes ne sont recopiées nulle part : leurs
 * cellules sont abandonnées dès la ligne lue.
 */
export function cleanExport(text: string): CleanedExport {
  const rows = parseCsv(text);
  if (!rows.length) return { payments: [], dropped: [], missing: [...ALLOWED_COLUMNS] };
  const header = rows[0].map((h) => h.trim());
  const index = new Map<string, number>();
  header.forEach((h, i) => { if ((ALLOWED_COLUMNS as readonly string[]).includes(h)) index.set(h, i); });
  const missing = ALLOWED_COLUMNS.filter((c) => !index.has(c));
  const dropped = header.filter((h) => !(ALLOWED_COLUMNS as readonly string[]).includes(h));
  if (missing.length) return { payments: [], dropped, missing };
  const cell = (r: string[], c: (typeof ALLOWED_COLUMNS)[number]) => (r[index.get(c)!] ?? '').trim();
  const payments: TebexPayment[] = [];
  for (const r of rows.slice(1)) {
    payments.push({
      id: cell(r, 'Id'),
      transactionId: cell(r, 'Transaction ID'),
      date: cell(r, 'Date'),
      status: cell(r, 'Status'),
      gateway: cell(r, 'Gateway'),
      username: cell(r, 'Username'),
      xuid: cell(r, 'Uuid'),
      packages: cell(r, 'Packages').split(',').map((x) => x.trim()).filter(Boolean),
      packageNames: splitNames(cell(r, 'Package Names')),
    });
  }
  return { payments, dropped, missing: [] };
}

// ==================== Règles de la boutique ====================

export interface ImportOptions {
  /** Pack Ultimate V1 (et Pack Ultimate sans version) donne Divinité 6 mois. Question ouverte : non par défaut. */
  ultimateV1: boolean;
  /** Deux achats du même grade s'additionnent (le second part de la fin du premier). */
  cumulate: boolean;
  /** Groupe LuckPerms de « Grade Paladium » et de « Grade Titan ». */
  paladiumGrade: SoldGrade;
  titanGrade: SoldGrade;
  /** Commandes manuelles écartées (tests), par pseudo Tebex. */
  excludeManualFrom: string[];
}

export const DEFAULT_OPTIONS: ImportOptions = {
  ultimateV1: false,
  cumulate: true,
  paladiumGrade: 'paladin',
  titanGrade: 'titan',
  excludeManualFrom: ['laitfrais'],
};

interface GrantRule { grade: SoldGrade; months: number }

/** Produit de la boutique : nom propre (les noms de l'export ont des accents abîmés) et grades donnés. */
interface ProductRule { label: string; grants: (o: ImportOptions) => GrantRule[] }

const six = (grade: SoldGrade): GrantRule[] => [{ grade, months: 6 }];

/** Produits connus, par numéro Tebex (colonne Packages). Un produit absent ne donne aucun grade. */
export const PRODUCTS: Record<string, ProductRule> = {
  '7334723': { label: 'Héros', grants: () => six('heros') },
  '7334734': { label: 'Légende', grants: () => six('legende') },
  '7334736': { label: 'Divinité', grants: () => six('divinite') },
  '7152334': { label: 'Grade Paladium', grants: (o) => six(o.paladiumGrade) },
  '7152332': { label: 'Grade Titan', grants: (o) => six(o.titanGrade) },
  '7338881': { label: 'Premium (1 mois)', grants: () => [{ grade: 'premium', months: 1 }] },
  '7338884': { label: 'Premium (3 mois)', grants: () => [{ grade: 'premium', months: 3 }] },
  '7338882': { label: 'Premium (6 mois)', grants: () => [{ grade: 'premium', months: 6 }] },
  '7756244': { label: 'Pack Ultimate V2', grants: () => six('divinite') },
  '7756245': { label: 'Pack Suprême V2', grants: () => [{ grade: 'premium', months: 3 }] },
  '7334746': { label: 'Pack Starter V1', grants: () => [] },
  '7334743': { label: 'Pack Ultimate V1', grants: (o) => (o.ultimateV1 ? six('divinite') : []) },
  '7152337': { label: 'Pack Ultimate', grants: (o) => (o.ultimateV1 ? six('divinite') : []) },
  '7756255': { label: 'LuckyPass (1 mois)', grants: () => [{ grade: 'luckypass', months: 1 }] },
};

/** Nom de produit sans accents, caractères abîmés ni casse, pour reconnaître un numéro inconnu. */
export function foldName(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/�/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

const BY_NAME = new Map(Object.values(PRODUCTS).map((p) => [foldName(p.label), p]));
// « Héros » abîmé en « H�ros » donne « hros » une fois plié : le nom plié de l'export y correspond aussi.
for (const p of Object.values(PRODUCTS)) BY_NAME.set(foldName(p.label.replace(/[éèêëÉàâîïôûùç]/g, '')), p);

function productOf(id: string, name: string | undefined): ProductRule | undefined {
  return PRODUCTS[id] ?? (name ? BY_NAME.get(foldName(name)) : undefined);
}

/** Date + n mois du calendrier (UTC), jour ramené à la fin d'un mois plus court. */
export function addMonths(epochSeconds: number, months: number): number {
  const d = new Date(epochSeconds * 1000);
  const day = d.getUTCDate();
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1, d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()));
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  t.setUTCDate(Math.min(day, last));
  return Math.floor(t.getTime() / 1000);
}

export interface Purchase { id: string; date: number; product: string }
export interface ExpectedGrant {
  grade: SoldGrade;
  end: number;
  purchases: Purchase[];
  /**
   * Grade à donner : le plus haut, un plus bas qui dure plus longtemps, Premium, LuckyPass. Un grade en
   * cours mais dominé (Héros acheté sous une Divinité qui dure plus longtemps) n'est jamais posé ; s'il est
   * déjà dans LuckPerms, il reçoit seulement sa date de fin.
   */
  needed: boolean;
}
export interface ExpectedPlayer {
  xuid: string;
  /** Pseudo du paiement le plus récent. */
  username: string;
  /**
   * Grades des achats encore en cours, ceux à donner d'abord (`needed`). Vide pour un joueur dont tous les
   * achats sont finis : il n'est envoyé que pour expliquer les grades qu'il porte encore.
   */
  grants: ExpectedGrant[];
  /** Grades vendus achetés un jour, même finis : ce qui explique un grade de LuckPerms. */
  explained: SoldGrade[];
}

export interface ExpectedStats {
  payments: number;
  counted: number;
  declined: number;
  excludedManual: number;
  /** Achats de Pack Ultimate V1 (ou sans version) qui ne donnent rien avec les options choisies. */
  ultimateIgnored: number;
  /** Produits sans règle (clés, points, PalaPass...) : aucun grade. */
  noGrade: Record<string, number>;
  /** Joueurs à qui un grade est dû, et par grade le plus haut. */
  players: number;
  byTopGrade: Partial<Record<SoldGrade, number>>;
  premiumOnly: number;
  premiumPlus: number;
  luckypass: number;
}

/**
 * Grades encore dus à chaque joueur. Comptent les paiements « Complete » (les « Manual », donnés par le
 * staff, en font partie), sauf les commandes manuelles écartées. Un grade court à partir de la date
 * d'achat ; avec `cumulate`, un second achat du même grade part de la fin du premier.
 */
export function computeExpected(payments: TebexPayment[], options: ImportOptions, nowSec: number): { players: ExpectedPlayer[]; stats: ExpectedStats } {
  const stats: ExpectedStats = {
    payments: payments.length, counted: 0, declined: 0, excludedManual: 0, ultimateIgnored: 0, noGrade: {},
    players: 0, byTopGrade: {}, premiumOnly: 0, premiumPlus: 0, luckypass: 0,
  };
  const excluded = new Set(options.excludeManualFrom.map((n) => n.trim().toLowerCase()).filter(Boolean));
  const buys = new Map<string, Map<SoldGrade, Array<Purchase & { months: number }>>>();
  const lastName = new Map<string, { date: number; name: string }>();
  for (const p of payments) {
    if (p.status !== 'Complete') { stats.declined++; continue; }
    if (p.gateway === 'Manual' && excluded.has(p.username.toLowerCase())) { stats.excludedManual++; continue; }
    const date = Math.floor(Date.parse(p.date) / 1000);
    if (!p.xuid || Number.isNaN(date)) continue;
    stats.counted++;
    const prev = lastName.get(p.xuid);
    if (!prev || date >= prev.date) lastName.set(p.xuid, { date, name: p.username });
    p.packages.forEach((id, i) => {
      const rule = productOf(id, p.packageNames[i]);
      const label = rule?.label ?? p.packageNames[i] ?? id;
      const grants = rule ? rule.grants(options) : [];
      if (!grants.length) {
        if (rule && /Ultimate/.test(rule.label) && !options.ultimateV1) stats.ultimateIgnored++;
        else if (!rule || !/Starter/.test(rule.label)) stats.noGrade[label] = (stats.noGrade[label] ?? 0) + 1;
        return;
      }
      for (const g of grants) {
        const byGrade = buys.get(p.xuid) ?? new Map();
        buys.set(p.xuid, byGrade);
        const list = byGrade.get(g.grade) ?? [];
        list.push({ id: p.transactionId || p.id, date, product: label, months: g.months });
        byGrade.set(g.grade, list);
      }
    });
  }

  const players: ExpectedPlayer[] = [];
  for (const [xuid, byGrade] of buys) {
    const active: ExpectedGrant[] = [];
    for (const [grade, list] of byGrade) {
      list.sort((a, b) => a.date - b.date);
      let end = 0;
      for (const b of list) {
        end = options.cumulate ? addMonths(Math.max(b.date, end), b.months) : Math.max(end, addMonths(b.date, b.months));
      }
      if (end > nowSec) active.push({ grade, end, needed: false, purchases: list.map(({ id, date, product }) => ({ id, date, product })) });
    }
    // Grade le plus haut, puis un grade plus bas seulement s'il dure plus longtemps que tous les plus hauts.
    const ranked = active.filter((g) => GRADE_RANK[g.grade] !== undefined).sort((a, b) => GRADE_RANK[b.grade]! - GRADE_RANK[a.grade]!);
    const needed: ExpectedGrant[] = [];
    let reach = 0;
    for (const g of ranked) {
      if (g.end > reach) { needed.push(g); reach = g.end; }
    }
    needed.push(...active.filter((g) => GRADE_RANK[g.grade] === undefined));
    for (const g of needed) g.needed = true;
    players.push({
      xuid,
      username: lastName.get(xuid)?.name ?? '',
      grants: [...needed, ...active.filter((g) => !g.needed)],
      explained: [...byGrade.keys()].sort(),
    });
    if (!needed.length) continue;
    stats.players++;
    const top = ranked[0]?.grade;
    if (top) stats.byTopGrade[top] = (stats.byTopGrade[top] ?? 0) + 1;
    const premium = active.some((g) => g.grade === 'premium');
    if (premium && top) stats.premiumPlus++;
    if (premium && !top) stats.premiumOnly++;
    if (active.some((g) => g.grade === 'luckypass')) stats.luckypass++;
  }
  // Joueurs à qui un grade est dû d'abord ; ceux dont les achats sont finis ensuite.
  players.sort((a, b) => Number(b.grants.length > 0) - Number(a.grants.length > 0) || a.username.localeCompare(b.username, 'fr'));
  return { players, stats };
}

// ==================== Aperçu renvoyé par le satellite ====================

export type TebexCategory = 'OK' | 'COUVERT' | 'ABSENT' | 'DATE_FAUSSE' | 'A_VIE' | 'MAUVAIS_GRADE' | 'FIN_TROP_TOT';

export const CATEGORY_LABELS: Record<TebexCategory, string> = {
  OK: 'OK',
  COUVERT: 'OK, couvert par un grade plus haut',
  ABSENT: 'Grade absent',
  DATE_FAUSSE: 'Date fausse (an 58 000)',
  A_VIE: 'À vie au lieu d\'une fin',
  MAUVAIS_GRADE: 'Mauvais grade',
  FIN_TROP_TOT: 'Fin trop proche',
};

/** Phrase de ce que la catégorie veut dire pour le joueur. */
export const CATEGORY_HINTS: Record<TebexCategory, string> = {
  OK: 'le joueur a son grade jusqu\'à la bonne date',
  COUVERT: 'un grade plus haut lui donne déjà tout jusqu\'à cette date',
  ABSENT: 'achat jamais livré (file Tebex ou boîte aux lettres bloquée) : il n\'a pas le grade',
  DATE_FAUSSE: 'date écrite en millisecondes : pour le jeu, le grade ne finit jamais',
  A_VIE: 'grade arrivé sans durée (champ « experity ») : il le garde pour toujours',
  MAUVAIS_GRADE: 'il a un grade plus bas que celui acheté',
  FIN_TROP_TOT: 'son grade finit avant la date due (durée ou cumul non appliqués)',
};

export const CATEGORY_ORDER: TebexCategory[] = ['ABSENT', 'MAUVAIS_GRADE', 'DATE_FAUSSE', 'FIN_TROP_TOT', 'A_VIE', 'COUVERT', 'OK'];
